import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function CoworkingVancouver() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "coworking-in-vancouver";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext() || {};
  const ref_data = articles ? articles.find((art) => art.slug === ref_id) : null;

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "section-1": "Best Coworking Spaces",
    "section-2": "Local Regulations & Etiquette",
    faq: "FAQ",
    "local-regulations": "Local Regulations (Additional)",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  const referencesData = [
    {
      url: "https://vancouver.ca/doing-business.aspx",
      label: "City of Vancouver – Business Licensing",
    },
    {
      url: "https://www.coworkingcafe.com/canada/bc/vancouver/",
      label: "CoworkingCafe – Vancouver Listings",
    },
    {
      url: "https://hivevancouver.com/",
      label: "HiVE Vancouver (Official Website)",
    },
    {
      text: "Disclaimer: Information (addresses, hours, prices) may change. Always confirm details with the coworking space.",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  // The conversation mentioned multiple locations; we’ll map them to relevant sections.
  // We'll use IDs that match our “Best Coworking Spaces” section (section-1).
  // For demonstration, we’ll place a marker for each location mentioned, referencing approximate coordinates from the conversation.

  const locations = [
    {
      id: "net-hub",
      lat: 49.28415,
      lng: -123.11205,
      popupText: "The Network Hub",
    },
    {
      id: "hive",
      lat: 49.28357,
      lng: -123.11144,
      popupText: "HiVE Vancouver",
    },
    {
      id: "latelier",
      lat: 49.28210,
      lng: -123.11020,
      popupText: "L’Atelier Coworking",
    },
    {
      id: "werklab",
      lat: 49.27691,
      lng: -123.07780,
      popupText: "Werklab",
    },
    {
      id: "suite-genius-kits",
      lat: 49.26850,
      lng: -123.15350,
      popupText: "Suite Genius (Kitsilano)",
    },
    {
      id: "suite-genius-mp",
      lat: 49.26310,
      lng: -123.10800,
      popupText: "Suite Genius (Mount Pleasant)",
    },
    {
      id: "wework-bentall",
      lat: 49.28592,
      lng: -123.11942,
      popupText: "WeWork – Bentall III",
    },
    {
      id: "iq-offices",
      lat: 49.28450,
      lng: -123.12070,
      popupText: "iQ Offices",
    },
    {
      id: "pavilion",
      lat: 49.26680,
      lng: -123.10450,
      popupText: "Pavilion Cowork",
    },
    {
      id: "hervana",
      lat: 49.28370,
      lng: -123.11750,
      popupText: "Hervana Coworking Collective",
    },
    {
      id: "cube-creative",
      lat: 49.26690,
      lng: -123.10950,
      popupText: "CUBE Creative Coworking",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>Coworking in Vancouver: Best Spaces & Tips</title>
        <meta
          name="description"
          content="Explore Vancouver’s top coworking spaces, local regulations, FAQs, and actionable tips for freelancers, startups, and remote workers."
        />
        <meta
          name="keywords"
          content="coworking in vancouver, remote work, freelancers, startups, coworking spaces"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/coworking-in-vancouver.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="Coworking in Vancouver: Best Spaces & Tips"
        />
        <meta
          property="og:description"
          content="Discover Vancouver’s coworking scene: top picks, regulations, FAQs, and more."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/coworking-in-vancouver.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/coworking-in-vancouver.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Coworking in Vancouver: Best Spaces & Tips"
        />
        <meta
          name="twitter:description"
          content="A practical guide to coworking in Vancouver, perfect for freelancers, startups, and remote teams."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/coworking-in-vancouver.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={
                ref_data?.image ||
                "https://bcouver.com/assets/images/article-fallback.jpg"
              }
              alt="Coworking in Vancouver - Shared Office Space"
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia />

            {/* SECTION: Introduction */}
            <h2 id="intro" ref={(el) => setSpotRef("intro", el)}>
              Introduction
            </h2>
            <p>
              Vancouver’s coworking scene has grown rapidly in recent years,
              offering flexible workspace solutions for freelancers, remote
              workers, and startups. With a vibrant tech and creative community
              set against the stunning backdrop of the city’s mountains and
              waterfront, Vancouver provides a compelling environment for
              productivity and networking. This guide covers the best coworking
              spaces in town, local rules and etiquette, and answers to common
              FAQs. Whether you’re a digital nomad or local entrepreneur,
              Vancouver’s coworking options can help you find a productive and
              inspiring workspace.
            </p>

            {/* SECTION: Best Coworking Spaces */}
            <h2
              id="section-1"
              ref={(el) => setSpotRef("section-1", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              Best Coworking Spaces
            </h2>

            <p>
              Below is a curated list of Vancouver’s top coworking spaces,
              catering to different needs—from affordability and community vibe
              to premium amenities and corporate sophistication.
            </p>

            <h3>The Network Hub</h3>
            <p>
              <strong>Address:</strong> 422 Richards Street #170, Vancouver, BC
              V6B 2Z4 <br />
              <strong>Hours:</strong> Mon–Fri: 9:00am–5:00pm (staffed);
              after-hours access for members.
            </p>
            <p
              ref={(el) => setSpotRef("net-hub", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <Marker
                coordY={49.28415}
                coordX={-123.11205}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              Known for its professional atmosphere and downtown location, The
              Network Hub offers hot desks, dedicated desks, and private
              offices. Members enjoy conference rooms, a lounge area, and
              networking events. It’s well-suited for entrepreneurs who want a
              central address and a thriving community.
            </p>

            <h3>HiVE Vancouver</h3>
            <p>
              <strong>Address:</strong> Suite 300 – 420 West Hastings Street,
              Vancouver, BC V6B 1L1 <br />
              <strong>Hours:</strong> Weekdays, with 24/7 access for members.
            </p>
            <p
              ref={(el) => setSpotRef("hive", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <Marker
                coordY={49.28357}
                coordX={-123.11144}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              A community-driven coworking space focusing on social impact,
              HiVE Vancouver welcomes non-profits, creatives, and freelancers.
              With a cozy ambiance, regular workshops, and events, it fosters a
              collaborative environment perfect for those seeking a
              mission-driven community vibe.
            </p>

            <h3>L’Atelier Coworking</h3>
            <p>
              <strong>Address:</strong> #400 – 319 West Hastings Street,
              Vancouver, BC V6B 1H6 <br />
              <strong>Hours:</strong> Mon–Fri (day passes), 24/7 for full
              members.
            </p>
            <p
              ref={(el) => setSpotRef("latelier", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <Marker
                coordY={49.28210}
                coordX={-123.11020}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              This boutique space in Gastown caters to a creative crowd. It
              features open desks, phone booths, a meeting room, and a bright,
              welcoming layout. L’Atelier also partners with other coworking
              spaces across Canada, giving members added flexibility and
              mobility.
            </p>

            <h3>Werklab</h3>
            <p>
              <strong>Address:</strong> #290 – 1275 Venables Street, Vancouver,
              BC V6A 2C9 <br />
              <strong>Hours:</strong> Mon–Fri (staffed), 24/7 for members.
            </p>
            <p
              ref={(el) => setSpotRef("werklab", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <Marker
                coordY={49.27691}
                coordX={-123.07780}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              A wellness-focused coworking space, Werklab integrates mindfulness
              and community-building with a traditional office setup. Expect
              meditation rooms, on-site yoga, and holistic workshops alongside
              standard amenities like fast Wi-Fi and dedicated desks.
            </p>

            <h3>Suite Genius (Kitsilano & Mount Pleasant)</h3>
            <p>
              <strong>Locations:</strong> Kitsilano (202–1965 West 4th Avenue)
              & Mount Pleasant (300–225 West 8th Avenue)
            </p>
            <p
              ref={(el) => setSpotRef("suite-genius-kits", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <Marker
                coordY={49.26850}
                coordX={-123.15350}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              <strong>Kitsilano Branch:</strong> Near 4th Avenue shops and the
              beach. Ideal for those wanting a relaxed, neighborhood feel.
            </p>
            <p
              ref={(el) => setSpotRef("suite-genius-mp", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <Marker
                coordY={49.26310}
                coordX={-123.10800}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              <strong>Mount Pleasant Branch:</strong> Surrounded by breweries
              and trendy restaurants. Both locations offer hot desks, dedicated
              desks, and private offices, fostering a friendly, local community
              vibe.
            </p>

            <h3>WeWork – Bentall III</h3>
            <p>
              <strong>Address:</strong> 595 Burrard Street, Vancouver, BC V7X
              1L4 <br />
              <strong>Hours:</strong> 24/7 for members.
            </p>
            <p
              ref={(el) => setSpotRef("wework-bentall", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <Marker
                coordY={49.28592}
                coordX={-123.11942}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              A global coworking chain offering sleek lounges, private offices,
              phone booths, and stocked kitchens. WeWork Bentall III is in the
              downtown financial district, featuring abundant networking events
              and seamless transit connections.
            </p>

            <h3>iQ Offices</h3>
            <p>
              <strong>Address:</strong> 1055 West Georgia Street, Vancouver, BC
              V6E 3P3 <br />
              <strong>Hours:</strong> 24/7 for members, staffed weekdays.
            </p>
            <p
              ref={(el) => setSpotRef("iq-offices", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <Marker
                coordY={49.28450}
                coordX={-123.12070}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              For those seeking a premium experience, iQ Offices provides high
              floors with panoramic views, modern interiors, and comprehensive
              office services. Ideal for professionals needing a luxurious
              downtown setting without a long-term lease.
            </p>

            <h3>Pavilion Cowork</h3>
            <p>
              <strong>Address:</strong> 400–22 East 5th Avenue, Vancouver, BC
              V5T 1G8 <br />
              <strong>Hours:</strong> 24/7 for members (private members’ club
              model).
            </p>
            <p
              ref={(el) => setSpotRef("pavilion", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <Marker
                coordY={49.26680}
                coordX={-123.10450}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              An exclusive, design-forward space located in Mount Pleasant,
              offering upscale amenities and curated membership. Pavilion holds
              regular networking events and speaker sessions aimed at connecting
              entrepreneurs and innovators.
            </p>

            <h3>Hervana Coworking Collective</h3>
            <p>
              <strong>Address:</strong> 506 – 595 Howe Street, Vancouver, BC
              V6C 2T5 <br />
              <strong>Hours:</strong> Mon–Fri (daytime), 24/7 for members.
            </p>
            <p
              ref={(el) => setSpotRef("hervana", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <Marker
                coordY={49.28370}
                coordX={-123.11750}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              A women-focused coworking collective fostering a supportive space
              for female entrepreneurs and professionals. Hervana offers
              mentorship, small workshops, and a vibrant community that welcomes
              all who champion women in business.
            </p>

            <h3>CUBE Creative Coworking</h3>
            <p>
              <strong>Address:</strong> 363 West 6th Avenue, Vancouver, BC V5Y
              1L1 <br />
              <strong>Hours:</strong> 24/7 for members, day passes during
              business hours.
            </p>
            <p
              ref={(el) => setSpotRef("cube-creative", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <Marker
                coordY={49.26690}
                coordX={-123.10950}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              A smaller, budget-friendly coworking studio perfect for
              creatives, developers, and freelancers. CUBE focuses on a
              collaborative yet laid-back environment, offering shared desks,
              meeting areas, and basic amenities without breaking the bank.
            </p>

            {/* SECTION: Local Regulations & Etiquette */}
            <h2
              id="section-2"
              ref={(el) => setSpotRef("section-2", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              Local Regulations & Etiquette
            </h2>
            <p>
              In Vancouver, coworking spaces generally fall under standard
              commercial/office zoning. Operators must have a valid business
              license, and they typically comply with local fire codes,
              WorkSafeBC guidelines, and health regulations. Users are advised
              to abide by each space’s posted rules—particularly regarding noise,
              security, and shared amenities.
            </p>
            <p>
              Practicing good etiquette is crucial for maintaining a pleasant
              coworking environment. Keep noise to a minimum, respect quiet
              zones, clean up after yourself, and be mindful of shared resources
              like meeting rooms and printers. Many spaces have posted community
              guidelines, which can include scent-free policies or mandatory
              booking procedures for conference rooms. Always follow the rules
              to maintain a harmonious and productive atmosphere.
            </p>

            {/* SECTION: FAQ */}
            <h2 id="faq" ref={(el) => setSpotRef("faq", el)}>
              FAQ
            </h2>
            <div className="faq-section">
              <h3>1. What is the average cost of coworking in Vancouver?</h3>
              <p>
                Day passes generally range from CA$25 to $50, while monthly hot
                desk memberships run about $200–$400. Dedicated desks often
                start around $400–$600, and private offices can range from
                $800+.
              </p>

              <h3>2. Are these spaces secure?</h3>
              <p>
                Reputable coworking spaces have secure entry systems (keycards,
                CCTV) and staff onsite or on-call. Some provide lockers or
                dedicated office locks. Use a personal VPN if handling sensitive
                data, since most coworking Wi-Fi networks are shared.
              </p>

              <h3>3. Will I be able to network in a coworking space?</h3>
              <p>
                Yes! Most coworking spaces host events (workshops, socials,
                lunch-and-learns). Engaging with your coworkers and attending
                community events are great ways to connect with potential
                collaborators, clients, or friends.
              </p>
            </div>

            {/* SECTION: Local Regulations (Additional) */}
            <h2
              id="local-regulations"
              ref={(el) => setSpotRef("local-regulations", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              Local Regulations (Additional)
            </h2>
            <p>
              Coworking users do not typically need special permits to work in
              these spaces. However, if you are operating a business, ensure it
              meets any licensing requirements from the City of Vancouver. Check
              WorkSafeBC guidelines for workspace ergonomics and be aware that
              some spaces may impose health measures (e.g., mask guidelines or
              limited capacities) in response to evolving health advisories.
            </p>

            {/* SECTION: Conclusion */}
            <h2
              id="conclusion"
              ref={(el) => setSpotRef("conclusion", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              Conclusion
            </h2>
            <p>
              Vancouver’s coworking scene offers something for everyone—
              whether you need a casual, budget-friendly hot desk, a holistic
              workspace with wellness features, or a premium corporate suite.
              By understanding local regulations, practicing good coworking
              etiquette, and exploring the unique amenities of each space, you
              can find the perfect environment to stay productive, collaborate,
              and grow your network. Always check official websites or contact
              the providers for the latest details on membership, pricing, and
              health guidelines. Enjoy your coworking journey in Vancouver!
            </p>

            {/* BOTTOM COMPONENT: references, author, related articles, etc. */}
            <Bottom referencesData={referencesData} ref_data={ref_data} />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay
        ref={mobileMapRef}
        markers={locations}
        spotRefs={spotRefs}
      />

      <Footer />
    </>
  );
}